import './search-nav.web.css';

import { useCallback, useEffect, useRef, type ElementRef } from 'react';
import { Pressable, TextInput, View } from 'react-native';

import {
  SidebarDatabaseIcon,
  SidebarPageBlankIcon,
} from '@/components/layout/SidebarIcons.web';
import { Text } from '@/components/ui';
import { useSearchNav } from '@/lib/search/search-nav-context';
import type { SearchContainerResult } from '@/lib/search/types';

function ContainerIcon({ type }: { type: SearchContainerResult['containerType'] }) {
  return type === 'database' ? <SidebarDatabaseIcon /> : <SidebarPageBlankIcon />;
}

function matchedLabel(via: SearchContainerResult['matchedVia'][number]): string {
  if (via === 'fts') return 'Keyword';
  if (via === 'trigram') return 'Fuzzy';
  return 'Semantic';
}

export function SearchNavQueryBar() {
  const { query, setQuery, grouped, selectedIndex, setSelectedIndex, selectByIndex } =
    useSearchNav();
  const inputRef = useRef<ElementRef<typeof TextInput>>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onKeyPress = useCallback(
    (event: { nativeEvent: { key: string } }) => {
      const key = event.nativeEvent.key;
      if (key === 'ArrowDown') {
        if (grouped.length > 0) {
          setSelectedIndex((selectedIndex + 1) % grouped.length);
        }
      } else if (key === 'ArrowUp') {
        if (grouped.length > 0) {
          const next = (selectedIndex - 1 + grouped.length) % grouped.length;
          setSelectedIndex(next);
        }
      } else if (key === 'Enter' && grouped.length > 0) {
        selectByIndex(selectedIndex);
      }
    },
    [grouped.length, selectByIndex, selectedIndex, setSelectedIndex],
  );

  return (
    <View className="border-b border-border px-1.5 py-1.5">
      <TextInput
        ref={inputRef}
        className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-left text-sm text-fg"
        placeholder="Search pages and notes…"
        value={query}
        onChangeText={setQuery}
        onKeyPress={onKeyPress}
        autoFocus
      />
    </View>
  );
}

export function SearchNavResults() {
  const { query, grouped, loading, error, activeOpenId, selectedIndex, openTarget } =
    useSearchNav();

  return (
    <View className="search-nav-results flex w-full flex-col items-stretch gap-0.5 px-0.5 py-1 text-left">
      {loading ? (
        <Text variant="caption" className="search-nav-status px-1 text-left text-muted">
          Searching…
        </Text>
      ) : null}
      {error ? (
        <Text variant="caption" className="search-nav-status px-1 text-left text-danger">
          {error}
        </Text>
      ) : null}
      {!loading && !error && query.trim() && grouped.length === 0 ? (
        <Text variant="caption" className="search-nav-status px-1 text-left text-muted">
          No matches.
        </Text>
      ) : null}
      {!query.trim() ? (
        <Text variant="caption" className="search-nav-status px-1 text-left text-muted">
          Search across page titles and body text.
        </Text>
      ) : null}
      {grouped.map((row, index) => {
        const isActive = activeOpenId === row.containerId;
        const isSelected = selectedIndex === index;
        return (
          <Pressable
            key={row.containerId}
            className={`search-nav-result flex w-full flex-row items-start justify-start gap-1.5 rounded-md px-1.5 py-1.5 text-left ${
              isActive ? 'bg-accent-muted' : isSelected ? 'bg-surface-raised' : ''
            }`}
            onPress={() => openTarget(row)}
          >
            <View className="search-nav-result-icon mt-0.5 flex shrink-0 items-start justify-start">
              <ContainerIcon type={row.containerType} />
            </View>
            <View className="search-nav-result-body min-w-0 flex-1 items-start text-left">
              <View className="search-nav-result-title-row flex w-full flex-row items-start justify-start gap-1 text-left">
                <Text
                  variant="caption"
                  className="search-nav-result-title truncate text-left font-medium text-fg"
                >
                  {row.title}
                </Text>
                {row.matchCount > 1 ? (
                  <Text variant="caption" className="search-nav-result-count shrink-0 text-left text-muted">
                    {row.matchCount}
                  </Text>
                ) : null}
              </View>
              {row.snippet ? (
                <Text
                  variant="caption"
                  className="search-nav-result-snippet mt-0.5 line-clamp-2 text-left text-muted"
                >
                  {row.snippet}
                </Text>
              ) : null}
              <View className="search-nav-result-badges mt-0.5 flex w-full flex-row flex-wrap justify-start gap-0.5">
                {row.matchedVia.map((via) => (
                  <Text
                    key={via}
                    variant="caption"
                    className="search-nav-result-badge rounded bg-surface-raised px-1 text-left text-[10px] text-fg-muted"
                  >
                    {matchedLabel(via)}
                  </Text>
                ))}
              </View>
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}
